/**
 * Identity and session: `/auth/login`, `/auth/callback`, `/auth/logout`,
 * `/api/me` and `/health` (Requirement 1, design.md API surface).
 *
 * The browser never sees a token. `/auth/login` and `/auth/callback` answer with
 * a redirect rather than a body, so what they need a schema for is their *query*:
 * the retained target of R1.1 and the authorization-code exchange of R1.2. The
 * PKCE verifier and the target itself live server-side in `oidc_state`, which is
 * why neither appears in a response shape here.
 */
import { z } from 'zod';
import { capabilitySchema, comPlanRoleSchema } from './enums.js';
import { idSchema, targetPathSchema } from './primitives.js';
/**
 * `GET /auth/login`. The screen the user asked for, retained for the duration of
 * the authentication exchange (R1.1).
 *
 * Application-relative only, so a crafted value cannot turn the return leg into
 * an open redirect.
 */
export const loginQuerySchema = z.object({
    target: targetPathSchema.nullable().default(null),
    /**
     * Requests OIDC's `prompt=login`: re-authenticate even if the authorization
     * server already holds a session for this browser.
     *
     * A closed enum of one value rather than a free string, so the only thing a
     * caller can ask for is *more* interaction. `prompt=none` in particular must
     * not be reachable from a query parameter - it asks the authorization server to
     * answer without interacting, which is a silent-SSO probe rather than anything
     * a sign-in link should be able to trigger.
     */
    prompt: z.literal('login').nullable().default(null),
    /**
     * OIDC's `login_hint`: which account the user is expected to sign in as, passed
     * to the authorization server so it can pre-select or pre-fill it.
     *
     * A hint only, and never a credential or an authorization: the authorization
     * server still authenticates the user, and the resulting `sub` claim - not this
     * value - is what the session and the role resolution are built from. A crafted
     * hint therefore cannot select somebody else's identity; at most it changes
     * which username a sign-in form starts out showing.
     */
    loginHint: z.string().min(1).max(320).nullable().default(null),
});
/**
 * `GET /auth/callback`. The identity provider's response leg (R1.2-R1.4).
 *
 * `code` and `error` are both optional because exactly one of them arrives, and a
 * failed assertion must be handled rather than rejected as a malformed request:
 * R1.4 requires a generic failure message and an audit entry carrying the
 * timestamp and correlation id only. `state` is always present - without it there
 * is no `oidc_state` row to check the 30-second expiry against.
 */
export const authCallbackQuerySchema = z.object({
    state: z.string().min(1),
    code: z.string().min(1).nullable().default(null),
    error: z.string().min(1).nullable().default(null),
});
/**
 * `POST /auth/logout`. The server-side session row is gone; `endSessionUrl` is
 * where the client continues for RP-initiated logout at the identity provider
 * (R1.6).
 */
export const logoutResponseSchema = z.object({
    signedOut: z.literal(true),
    endSessionUrl: z.string().min(1).nullable().default(null),
});
/**
 * `GET /api/me`. The Principal as the client may see it (design.md,
 * "Identity and session").
 *
 * Roles are resolved per request from the stored assignments and the group
 * mapping, not from a claim cached at login, which is what makes R1.10 hold
 * without a sign-out. The client uses `capabilities` to decide which controls to
 * render; the server checks the same capabilities before any handler runs, so a
 * client that renders a control it should not cannot get past the route.
 *
 * The zero-role principal of R1.3 is a first-class value here: empty `roles`,
 * empty `capabilities`, empty scopes, and `accessRequestPath` naming where to ask
 * for access.
 */
export const meResponseSchema = z.object({
    userId: idSchema,
    /** The Identity_Service subject (Entra `oid`). */
    subject: z.string().min(1),
    roles: z.array(comPlanRoleSchema),
    capabilities: z.array(capabilitySchema),
    /** Recorded in role administration; gates the transitions of R9.3. */
    approverRights: z.boolean(),
    /** Platform Owner read scope (R2.4). Empty for a principal holding no such role. */
    platformScope: z.array(idSchema),
    /** Channel Owner read scope (R2.4). */
    channelScope: z.array(idSchema),
    /** Where a user with no role assignment requests one (R1.3). */
    accessRequestPath: targetPathSchema.nullable().default(null),
});
/**
 * `GET /health`. Liveness and readiness, and deliberately no data: the endpoint
 * is unauthenticated, so anything beyond a state word would be readable by
 * anyone.
 */
export const healthResponseSchema = z.object({
    status: z.enum(['ok', 'degraded']),
});
//# sourceMappingURL=identity.js.map